import { FC, memo, useState, useEffect } from 'react';
import { TextField, FormHelperText } from '@mui/material';
import { TaskAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import './AddProjectDialog.scss';
import { BasicDialog } from '../../common/BasicDialog';

interface AddProjectDialogProps {
  visible: boolean;
  isAdding?: boolean;
  existingProjectNames: string[];
  onAdd: (name: string) => Promise<void | boolean> | void | boolean;
  onClose: () => void;
}

const AddProjectDialog: FC<AddProjectDialogProps> = memo(
  ({ isAdding = false, visible, existingProjectNames, onAdd, onClose }) => {
    const { t } = useTranslation();
    const [projectName, setProjectName] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      if (visible) {
        setProjectName('');
        setError(null);
      }
    }, [visible]);

    const validateProjectName = (name: string): boolean => {
      if (!name.trim()) {
        setError(t('task.project.nameRequired'));
        return false;
      }

      if (existingProjectNames.includes(name.trim())) {
        setError(t('task.project.nameExists'));
        return false;
      }

      setError(null);
      return true;
    };

    const handleClose = () => {
      onClose();
      setProjectName('');
      setError(null);
    };

    const handleAdd = async () => {
      if (!validateProjectName(projectName)) {
        return;
      }
      const shouldClose = await onAdd(projectName);
      if (shouldClose === false) {
        return;
      }
      handleClose();
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      setProjectName(newName);
      validateProjectName(newName);
    };

    return (
      <BasicDialog
        visible={visible}
        title={t('task.project.addProjectTitle')}
        ctaString={t('task.common.create')}
        isLoading={isAdding}
        disabled={!!error}
        onYes={handleAdd}
        onClose={handleClose}
        width="sm"
      >
        <div className="add-project-dialog__content">
          <TextField
            autoFocus
            required
            value={projectName}
            onChange={handleNameChange}
            margin="dense"
            id="name"
            name="name"
            label={t('task.project.name')}
            type="text"
            fullWidth
            variant="standard"
            error={!!error}
            className="add-project-dialog__content__input"
          />
          {error && <FormHelperText error>{error}</FormHelperText>}
          <div className="add-project-dialog__content__app">
            <TaskAlt className="add-project-dialog__content__app__icon" />
            <p>Lifestack</p>
          </div>
        </div>
      </BasicDialog>
    );
  }
);

export default AddProjectDialog;
